/**
 * New-item intake (invariant 6, SPEC §6.2).
 *
 * > New-item intake caps at 10/day, adjustable, and auto-suppresses when the due queue would
 * > exceed 15 estimated minutes. **Reviews are never cut to make room for new items.**
 *
 * The second sentence is the one that shapes this file. `planIntake` returns the due queue it was
 * given, unchanged and by identity, alongside whatever new items survived the caps. It has no path
 * that can shorten, reorder, or filter the reviews — not as a fallback, not under load, not when
 * the numbers are bad. Everything here only ever reduces the *new* list.
 *
 * Pure. `now` comes from the caller's injected clock.
 */

import type { Attempt, ItemRecord } from '../store/schema';
import { estimatedMinutes, estimatedSeconds, type SecondsPerItem } from './queue';

/** Invariant 6, as numbers. Both adjustable; the four-week review offers changes to the first. */
export const NEW_CAP_PER_DAY = 10;
export const INTAKE_SUPPRESS_MINUTES = 15;

/**
 * The intake "day" is a rolling 24 hours, not a calendar day.
 *
 * A calendar day needs a timezone, and a timezone boundary is something to be on the wrong side
 * of: usage scenario 1 is a 3:40 a.m. feed, which is a different calendar day from the evening
 * session four hours earlier and obviously the same stretch of sleep-deprived learning. A rolling
 * window also removes the midnight double-dip, where 20 new items arrive in two hours. See
 * DECISIONS D-041.
 */
export const INTAKE_WINDOW_MS = 86_400_000;

export interface IntakeConfig {
  newCapPerDay: number;
  suppressAboveMinutes: number;
  windowMs: number;
}

export const DEFAULT_INTAKE_CONFIG: IntakeConfig = Object.freeze({
  newCapPerDay: NEW_CAP_PER_DAY,
  suppressAboveMinutes: INTAKE_SUPPRESS_MINUTES,
  windowMs: INTAKE_WINDOW_MS,
});

/**
 * Why intake was limited. Surfaced in telemetry and on the reveal card so a quiet week of no new
 * words is explained rather than mysterious.
 */
export type IntakeReason =
  /** Nothing limited it; the full daily allowance was available. */
  | 'ok'
  /** Reviews alone already exceed the minutes threshold. Intake fully suppressed. */
  | 'queue-too-deep'
  /** The daily allowance is spent. */
  | 'daily-cap'
  /** Some room, but not the full allowance, before the minutes threshold. */
  | 'minutes-budget'
  /** Nothing new is left to introduce. */
  | 'no-candidates';

export interface IntakePlan {
  /** The due queue, unchanged. Identical by reference to the input — reviews are never cut. */
  reviews: readonly ItemRecord[];
  /** New items admitted this run, in the order given. */
  newItems: ItemRecord[];
  suppressed: boolean;
  reason: IntakeReason;
  /** Introductions inside the rolling window, before this plan. */
  introducedInWindow: number;
  /** Remaining daily allowance, after the cap but before the minutes budget. */
  dailyAllowanceRemaining: number;
  estimatedReviewMinutes: number;
}

export interface IntakeInput {
  /** Due reviews. Never modified. */
  dueQueue: readonly ItemRecord[];
  /** Items in the `new` state, already ordered by content priority (function words first). */
  candidates: readonly ItemRecord[];
  /** Epoch ms of every new-item introduction so far. See `introductionTimes`. */
  introducedAt: readonly number[];
  now: number;
  measured?: SecondsPerItem;
  config?: IntakeConfig;
}

/**
 * When each item was first introduced, derived from the attempt log.
 *
 * Derived rather than stored: the attempt log is the record of what actually happened, and a
 * separate `introducedAt` field on the item is a second source of truth that can disagree with it
 * after a partial write.
 */
export function introductionTimes(attempts: readonly Attempt[]): number[] {
  const first = new Map<string, number>();
  for (const attempt of attempts) {
    const seen = first.get(attempt.itemId);
    if (seen === undefined || attempt.at < seen) first.set(attempt.itemId, attempt.at);
  }
  return [...first.values()].sort((a, b) => a - b);
}

export function countInWindow(
  introducedAt: readonly number[],
  now: number,
  windowMs = INTAKE_WINDOW_MS,
): number {
  const since = now - windowMs;
  let count = 0;
  for (const at of introducedAt) if (at > since && at <= now) count += 1;
  return count;
}

/**
 * Decide how many new items may be introduced, and which.
 *
 * Order of limits, tightest last:
 *   1. Reviews alone over the minutes threshold → nothing new at all.
 *   2. The rolling daily cap.
 *   3. Whatever still fits inside the minutes budget alongside the reviews.
 *
 * Step 3 is a deliberate reading of "the due queue *would* exceed 15 estimated minutes": with 14
 * minutes of reviews waiting, admitting a full ten new items would push the session past the
 * threshold the invariant exists to defend. See DECISIONS D-042.
 */
export function planIntake(input: IntakeInput): IntakePlan {
  const cfg = input.config ?? DEFAULT_INTAKE_CONFIG;
  const measured = input.measured ?? {};
  const reviewMinutes = estimatedMinutes(input.dueQueue, measured);
  const introducedInWindow = countInWindow(input.introducedAt, input.now, cfg.windowMs);
  const dailyAllowanceRemaining = Math.max(0, cfg.newCapPerDay - introducedInWindow);

  const base = {
    // By reference, so a test can assert the reviews were not merely equal but untouched.
    reviews: input.dueQueue,
    introducedInWindow,
    dailyAllowanceRemaining,
    estimatedReviewMinutes: reviewMinutes,
  };

  if (reviewMinutes > cfg.suppressAboveMinutes) {
    return { ...base, newItems: [], suppressed: true, reason: 'queue-too-deep' };
  }
  if (dailyAllowanceRemaining === 0) {
    return { ...base, newItems: [], suppressed: true, reason: 'daily-cap' };
  }
  if (input.candidates.length === 0) {
    return { ...base, newItems: [], suppressed: false, reason: 'no-candidates' };
  }

  const budgetSeconds = (cfg.suppressAboveMinutes - reviewMinutes) * 60;
  const newItems: ItemRecord[] = [];
  let spent = 0;
  let stoppedOnBudget = false;

  for (const candidate of input.candidates) {
    if (newItems.length >= dailyAllowanceRemaining) break;
    const cost = estimatedSeconds(candidate, measured);
    if (spent + cost > budgetSeconds) {
      stoppedOnBudget = true;
      break;
    }
    spent += cost;
    newItems.push(candidate);
  }

  const hitDailyCap = newItems.length >= dailyAllowanceRemaining;
  const ranOutOfCandidates = newItems.length === input.candidates.length;

  const reason: IntakeReason = stoppedOnBudget
    ? 'minutes-budget'
    : hitDailyCap && introducedInWindow > 0
      ? 'daily-cap'
      : ranOutOfCandidates && newItems.length < dailyAllowanceRemaining
        ? 'no-candidates'
        : 'ok';

  return {
    ...base,
    newItems,
    // Suppression means intake was cut short by queue load or the cap — not that there happened
    // to be nothing left to teach.
    suppressed: newItems.length === 0 || stoppedOnBudget,
    reason,
  };
}
